import React, { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { format } from "date-fns"
import {
  AlertCircle,
  Briefcase,
  Building2,
  Calendar,
  ExternalLink,
  FileText,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  User,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Spinner } from "@/components/ui/spinner"
import { PersonCompliance } from "@/hooks/useComplianceRecords"
import { supabase } from "@/lib/supabase"
import { DvlaEndorsement, DvlaEntitlement } from "@/types/compliance"
import { SIGNED_URL_EXPIRY, createSignedDocumentUrl } from "@/utils/storageUtils"

interface CompliancePersonDrawerProps {
  person: PersonCompliance
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface DocumentPreview {
  type: string
  url: string | undefined
}

interface Application {
  id: string
  application_id: string | null
  status: string | null
  current_stage: string | null
  current_segment: string | null
  stage_note: string | null
  stage_reason: string | null
  created_at: string | null
}

const CompliancePersonDrawer: React.FC<CompliancePersonDrawerProps> = ({
  person,
  open,
  onOpenChange,
}) => {
  const [previewDoc, setPreviewDoc] = useState<DocumentPreview | null>(null)

  const personId = person?.id

  // Fetch DVLA documents
  const { data: documents, isLoading: documentsLoading } = useQuery({
    queryKey: ["compliance-person-documents", personId],
    queryFn: async () => {
      if (!personId) return []
      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .eq("person_id", personId)
        .in("type", ["dvla-front", "dvla-back"])
        .order("created_at", { ascending: false })

      if (error) throw error
      const docs = data || []
      return Promise.all(
        docs.map(async (doc) => {
          if (!doc.file) {
            return doc
          }
          const signedUrl = await createSignedDocumentUrl(doc.file)
          return { ...doc, file: signedUrl }
        }),
      )
    },
    enabled: open && !!personId,
    staleTime: SIGNED_URL_EXPIRY * 1000,
  })

  // Fetch applications
  const { data: applications, isLoading: applicationsLoading } = useQuery({
    queryKey: ["compliance-person-applications", personId],
    queryFn: async () => {
      if (!personId) return []
      const { data, error } = await supabase
        .from("applications")
        .select(
          "id, application_id, status, current_stage, current_segment, stage_note, stage_reason, created_at",
        )
        .eq("person_id", personId)
        .order("created_at", { ascending: false })

      if (error) throw error
      return (data || []) as Application[]
    },
    enabled: open && !!personId,
  })

  // Guard: Don't render if person is not available
  if (!person || !personId) {
    return null
  }

  const getDocumentData = (type: string) => {
    const doc = documents?.find((d) => d.type === type)
    if (!doc) return null
    return {
      url: doc.file || undefined,
      status: doc.status || undefined,
      uploadedAt: doc.created_at || undefined,
    }
  }

  const getStatusBadge = (status: string | null) => {
    if (!status) return null

    switch (status.toLowerCase()) {
      case "active":
        return <Badge className="bg-green-100 text-green-800">Active</Badge>
      case "inactive":
        return (
          <Badge className="bg-palette-jungleGreen100 text-palette-jungleGreen800">Inactive</Badge>
        )
      case "banned":
        return <Badge className="bg-red-100 text-red-800">Banned</Badge>
      default:
        return <Badge variant="secondary">{status}</Badge>
    }
  }

  const getApplicationStatusBadge = (status: string | null) => {
    if (!status) return <Badge variant="secondary">Unknown</Badge>

    switch (status.toLowerCase()) {
      case "approved":
      case "accepted":
        return <Badge className="bg-green-100 text-green-800">{status}</Badge>
      case "rejected":
      case "declined":
        return <Badge className="bg-red-100 text-red-800">{status}</Badge>
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-800">{status}</Badge>
      default:
        return <Badge variant="secondary">{status}</Badge>
    }
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A"
    try {
      return format(new Date(dateString), "dd/MM/yyyy")
    } catch {
      return "N/A"
    }
  }

  const isOnHoldOrRejected = (status: string | null) => {
    if (!status) return false
    const statusLower = status.toLowerCase()
    return (
      statusLower === "on hold" ||
      statusLower === "hold" ||
      statusLower === "rejected" ||
      statusLower === "declined"
    )
  }

  const getLicenceExpiry = (entitlements: DvlaEntitlement[] | null) => {
    if (!entitlements || entitlements.length === 0) return null
    const catB = entitlements.find((e) => e.categoryCode === "B")
    if (catB) return catB.expiryDate
    return entitlements[0].expiryDate
  }

  const getTotalPoints = (endorsements: DvlaEndorsement[] | null) => {
    if (!endorsements) return 0
    return endorsements.reduce((sum, e) => sum + e.penaltyPoints, 0)
  }

  const getStatusColor = (status: string | null) => {
    switch (status?.toLowerCase()) {
      case "valid":
      case "full":
        return "bg-green-100 text-green-800"
      case "provisional":
        return "bg-yellow-100 text-yellow-800"
      case "disqualified":
      case "revoked":
        return "bg-red-100 text-red-800"
      default:
        return "bg-palette-jungleGreen100 text-palette-jungleGreen800"
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader className="pb-2">
            <SheetTitle className="text-lg">
              {person.first_name} {person.surname}
            </SheetTitle>
            <SheetDescription className="text-xs">
              Person ID: {person.person_id || person.person || "N/A"}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-3 space-y-3">
            {/* Person Information Section */}
            <Card>
              <CardHeader className="pb-2 px-3 pt-3">
                <CardTitle className="text-sm font-semibold">Person Information</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 px-3 pb-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                    <div>
                      <div className="text-xs text-palette-jungleGreen500">Name</div>
                      <div className="text-sm font-medium">
                        {person.first_name} {person.surname}
                      </div>
                    </div>
                  </div>
                  {person.email && (
                    <div className="flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                      <div>
                        <div className="text-xs text-palette-jungleGreen500">Email</div>
                        <div className="text-sm font-medium">{person.email}</div>
                      </div>
                    </div>
                  )}
                  {person.phone_number && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                      <div>
                        <div className="text-xs text-palette-jungleGreen500">Phone</div>
                        <div className="text-sm font-medium">{person.phone_number}</div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <div>
                      <div className="text-xs text-palette-jungleGreen500">Status</div>
                      <div>{getStatusBadge(person.status)}</div>
                    </div>
                  </div>
                  {person.location && person.location.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                      <div>
                        <div className="text-xs text-palette-jungleGreen500">Location</div>
                        <div className="text-sm font-medium">{person.location.join(", ")}</div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Employment & Job Information Section */}
            <Card>
              <CardHeader className="pb-2 px-3 pt-3">
                <CardTitle className="text-sm font-semibold">
                  Employment & Job Information
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 px-3 pb-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {person.employer && (
                    <div className="flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                      <div>
                        <div className="text-xs text-palette-jungleGreen500">Employer</div>
                        <div className="text-sm font-medium">{person.employer}</div>
                      </div>
                    </div>
                  )}
                  {person.sheriff_employer && (
                    <div className="flex items-center gap-1.5">
                      <Briefcase className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                      <div>
                        <div className="text-xs text-palette-jungleGreen500">Sheriff Employer</div>
                        <div className="text-sm font-medium">{person.sheriff_employer}</div>
                      </div>
                    </div>
                  )}
                  {person.role && (
                    <div className="flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                      <div>
                        <div className="text-xs text-palette-jungleGreen500">Role</div>
                        <div className="text-sm font-medium">{person.role}</div>
                      </div>
                    </div>
                  )}
                  {person.sheriff_type && (
                    <div className="flex items-center gap-1.5">
                      <Briefcase className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                      <div>
                        <div className="text-xs text-palette-jungleGreen500">Sheriff Type</div>
                        <div className="text-sm font-medium">{person.sheriff_type}</div>
                      </div>
                    </div>
                  )}
                  {person.contract_type && (
                    <div className="flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                      <div>
                        <div className="text-xs text-palette-jungleGreen500">Contract Type</div>
                        <div className="text-sm font-medium">{person.contract_type}</div>
                      </div>
                    </div>
                  )}
                  {person.contract_start_date && (
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                      <div>
                        <div className="text-xs text-palette-jungleGreen500">Contract Start</div>
                        <div className="text-sm font-medium">
                          {formatDate(person.contract_start_date)}
                        </div>
                      </div>
                    </div>
                  )}
                  {person.contract_end_date && (
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                      <div>
                        <div className="text-xs text-palette-jungleGreen500">Contract End</div>
                        <div className="text-sm font-medium">
                          {formatDate(person.contract_end_date)}
                        </div>
                      </div>
                    </div>
                  )}
                  {person.payment_basis && (
                    <div className="flex items-center gap-1.5">
                      <Briefcase className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                      <div>
                        <div className="text-xs text-palette-jungleGreen500">Payment Basis</div>
                        <div className="text-sm font-medium">{person.payment_basis}</div>
                      </div>
                    </div>
                  )}
                  {person.sheriff_areas && person.sheriff_areas.length > 0 && (
                    <div className="flex items-start gap-1.5 md:col-span-2">
                      <MapPin className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-xs text-palette-jungleGreen500 mb-0.5">
                          Sheriff Areas
                        </div>
                        <div className="text-sm font-medium">{person.sheriff_areas.join(", ")}</div>
                      </div>
                    </div>
                  )}
                  {person.hpd_pool && (
                    <div className="flex items-center gap-1.5">
                      <Briefcase className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                      <div>
                        <div className="text-xs text-palette-jungleGreen500">HPD Pool</div>
                        <div className="text-sm font-medium">{person.hpd_pool}</div>
                      </div>
                    </div>
                  )}
                  {person.driver_manager_team && (
                    <div className="flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                      <div>
                        <div className="text-xs text-palette-jungleGreen500">
                          Driver Manager Team
                        </div>
                        <div className="text-sm font-medium">{person.driver_manager_team}</div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Driving Licence Checks Section */}
            {person.driver_licence_records && person.driver_licence_records.length > 0 && (
              <Card>
                <CardHeader className="pb-2 px-3 pt-3">
                  <CardTitle className="text-sm font-semibold">Driving Licence Checks</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 px-3 pb-3 space-y-2">
                  {person.driver_licence_records.map((record, idx) => {
                    const expiry = getLicenceExpiry(record.entitlement)
                    const points = getTotalPoints(record.endorsements)
                    return (
                      <div
                        key={record.id || idx}
                        className="border rounded-md p-2.5 bg-palette-jungleGreen000/50"
                      >
                        <div className="grid grid-cols-2 gap-3 mb-2">
                          <div>
                            <div className="text-xs text-palette-jungleGreen500 mb-0.5">
                              Licence Number
                            </div>
                            <div className="font-mono text-sm font-semibold">
                              {record.driving_licence_number || "N/A"}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-palette-jungleGreen500 mb-0.5">Status</div>
                            <Badge
                              className={`${getStatusColor(record.licence_status)} text-xs rounded-md px-2 py-0.5`}
                              variant="secondary"
                            >
                              {record.licence_status || "Unknown"}
                            </Badge>
                          </div>
                          <div>
                            <div className="text-xs text-palette-jungleGreen500 mb-0.5">
                              Expiry Date
                            </div>
                            <div className="text-sm font-semibold">
                              {expiry ? format(new Date(expiry), "dd/MM/yyyy") : "N/A"}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-palette-jungleGreen500 mb-0.5">
                              Total Points
                            </div>
                            <div className="text-sm font-semibold">
                              {points > 0 ? `${points} pts` : "0 pts"}
                            </div>
                          </div>
                        </div>
                        {record.created_at && (
                          <div className="text-xs text-palette-jungleGreen500 pt-1.5 border-t">
                            Checked: {format(new Date(record.created_at), "dd/MM/yyyy HH:mm")}
                          </div>
                        )}
                        {/* Endorsements for this licence record */}
                        {record.endorsements && record.endorsements.length > 0 && (
                          <div className="mt-2.5 pt-2.5 border-t space-y-2">
                            <div className="text-xs font-semibold text-palette-jungleGreen700 uppercase tracking-wide">
                              Endorsements ({record.endorsements.length})
                            </div>
                            {record.endorsements.map((endorsement, endIdx) => (
                              <div key={endIdx} className="border rounded-md p-2 bg-white">
                                <div className="flex justify-between items-start mb-1.5">
                                  <Badge
                                    variant="outline"
                                    className="font-mono text-xs rounded-md px-2 py-0.5"
                                  >
                                    {endorsement.offenceCode}
                                  </Badge>
                                  <Badge
                                    variant="destructive"
                                    className="text-xs rounded-md px-2 py-0.5"
                                  >
                                    {endorsement.penaltyPoints} pts
                                  </Badge>
                                </div>
                                <div className="text-xs font-semibold text-palette-jungleGreen900 mb-1.5">
                                  {endorsement.offenceLegalLiteral || "Unknown Offence"}
                                </div>
                                <div className="space-y-1 text-xs text-palette-jungleGreen600">
                                  {endorsement.offenceDate && (
                                    <div className="flex justify-between">
                                      <span className="text-palette-jungleGreen500">
                                        Offence Date:
                                      </span>
                                      <span className="font-medium">
                                        {format(new Date(endorsement.offenceDate), "dd/MM/yyyy")}
                                      </span>
                                    </div>
                                  )}
                                  {endorsement.convictionDate && (
                                    <div className="flex justify-between">
                                      <span className="text-palette-jungleGreen500">
                                        Conviction Date:
                                      </span>
                                      <span className="font-medium">
                                        {format(new Date(endorsement.convictionDate), "dd/MM/yyyy")}
                                      </span>
                                    </div>
                                  )}
                                  {endorsement.penaltyPointsExpiryDate && (
                                    <div className="flex justify-between">
                                      <span className="text-palette-jungleGreen500">
                                        Points Expiry:
                                      </span>
                                      <span className="font-medium">
                                        {format(
                                          new Date(endorsement.penaltyPointsExpiryDate),
                                          "dd/MM/yyyy",
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  {endorsement.disqualification && (
                                    <div className="flex justify-between">
                                      <span className="text-palette-jungleGreen500">
                                        Disqualification:
                                      </span>
                                      <span className="font-semibold text-red-600">
                                        {endorsement.disqualification.forLife
                                          ? "For Life"
                                          : `${endorsement.disqualification.years || 0}y ${endorsement.disqualification.months || 0}m ${endorsement.disqualification.days || 0}d`}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </CardContent>
              </Card>
            )}

            {/* DVLA Documents Section */}
            <Card>
              <CardHeader className="pb-2 px-3 pt-3">
                <CardTitle className="text-sm font-semibold">DVLA Documents</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 px-3 pb-3">
                {documentsLoading ? (
                  <div className="flex justify-center py-4">
                    <Spinner className="h-5 w-5" />
                  </div>
                ) : documents && documents.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {getDocumentData("dvla-front") && (
                      <div
                        className="border rounded-md p-2.5 cursor-pointer hover:ring-2 hover:ring-blue-300 hover:border-blue-400 transition-all bg-white"
                        onClick={() =>
                          setPreviewDoc({
                            type: "DVLA License (Front)",
                            url: getDocumentData("dvla-front")?.url,
                          })
                        }
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-palette-jungleGreen700">
                            Front
                          </span>
                          {getDocumentData("dvla-front")?.status && (
                            <Badge variant="secondary" className="text-xs rounded-md px-2 py-0.5">
                              {getDocumentData("dvla-front")?.status}
                            </Badge>
                          )}
                        </div>
                        {getDocumentData("dvla-front")?.url ? (
                          <img
                            src={getDocumentData("dvla-front")?.url}
                            alt="DVLA Front"
                            className="w-full h-28 object-contain border rounded-md bg-palette-jungleGreen000"
                          />
                        ) : (
                          <div className="w-full h-28 border rounded-md flex items-center justify-center bg-palette-jungleGreen000">
                            <p className="text-palette-jungleGreen400 text-xs">Not available</p>
                          </div>
                        )}
                      </div>
                    )}
                    {getDocumentData("dvla-back") && (
                      <div
                        className="border rounded-md p-2.5 cursor-pointer hover:ring-2 hover:ring-blue-300 hover:border-blue-400 transition-all bg-white"
                        onClick={() =>
                          setPreviewDoc({
                            type: "DVLA License (Back)",
                            url: getDocumentData("dvla-back")?.url,
                          })
                        }
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-palette-jungleGreen700">
                            Back
                          </span>
                          {getDocumentData("dvla-back")?.status && (
                            <Badge variant="secondary" className="text-xs rounded-md px-2 py-0.5">
                              {getDocumentData("dvla-back")?.status}
                            </Badge>
                          )}
                        </div>
                        {getDocumentData("dvla-back")?.url ? (
                          <img
                            src={getDocumentData("dvla-back")?.url}
                            alt="DVLA Back"
                            className="w-full h-28 object-contain border rounded-md bg-palette-jungleGreen000"
                          />
                        ) : (
                          <div className="w-full h-28 border rounded-md flex items-center justify-center bg-palette-jungleGreen000">
                            <p className="text-palette-jungleGreen400 text-xs">Not available</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-palette-jungleGreen500 text-xs py-3 text-center">
                    No DVLA documents found.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Applications Section */}
            <Card>
              <CardHeader className="pb-2 px-3 pt-3">
                <CardTitle className="text-sm font-semibold">Applications</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 px-3 pb-3">
                {applicationsLoading ? (
                  <div className="flex justify-center py-4">
                    <Spinner className="h-5 w-5" />
                  </div>
                ) : applications && applications.length > 0 ? (
                  <div className="space-y-2">
                    {applications.map((app) => (
                      <div
                        key={app.id}
                        className="border rounded-md p-2.5 hover:bg-palette-jungleGreen000/50 transition bg-white"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-1.5 flex-1 min-w-0">
                            <FileText className="h-3.5 w-3.5 text-palette-jungleGreen500 shrink-0" />
                            <span className="text-sm font-semibold truncate">
                              {app.application_id || `Application ${app.id.slice(0, 8)}`}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 shrink-0"
                            onClick={(e) => {
                              e.stopPropagation()
                              window.open(`/applications/${app.id}`, "_blank")
                            }}
                            title="Open application details in new tab"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {getApplicationStatusBadge(app.status)}
                          {app.current_stage && (
                            <Badge variant="outline" className="text-xs rounded-md px-2 py-0.5">
                              {app.current_stage}
                            </Badge>
                          )}
                          {app.current_segment && (
                            <Badge variant="outline" className="text-xs rounded-md px-2 py-0.5">
                              {app.current_segment}
                            </Badge>
                          )}
                        </div>
                        {isOnHoldOrRejected(app.status) && (
                          <div className="space-y-1.5 pt-2 border-t">
                            {app.stage_reason && (
                              <div className="flex items-start gap-1.5">
                                <AlertCircle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-semibold text-palette-jungleGreen500 mb-0.5">
                                    Reason
                                  </div>
                                  <div className="text-xs text-amber-600 font-semibold">
                                    {app.stage_reason}
                                  </div>
                                </div>
                              </div>
                            )}
                            {app.stage_note && (
                              <div className="flex items-start gap-1.5">
                                <MessageSquare className="h-3.5 w-3.5 text-palette-jungleGreen500 mt-0.5 shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-semibold text-palette-jungleGreen500 mb-0.5">
                                    Note
                                  </div>
                                  <div className="text-xs text-palette-jungleGreen700">
                                    {app.stage_note}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {app.created_at && (
                          <div className="flex items-center gap-1 text-xs text-palette-jungleGreen500 mt-2 pt-1.5 border-t">
                            <Calendar className="h-3 w-3" />
                            <span>Created: {formatDate(app.created_at)}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-palette-jungleGreen500 text-xs py-3 text-center">
                    No applications found.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </SheetContent>
      </Sheet>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={(open) => !open && setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewDoc?.type}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4">
            {previewDoc?.url ? (
              <img
                src={previewDoc.url}
                alt={previewDoc.type}
                className="max-w-full max-h-[70vh] object-contain border rounded"
              />
            ) : (
              <div className="w-64 h-40 border rounded flex items-center justify-center bg-palette-jungleGreen000">
                <p className="text-palette-jungleGreen500">Document not available</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPreviewDoc(null)}>
                Close
              </Button>
              {previewDoc?.url && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    window.open(previewDoc.url, "_blank")
                  }}
                >
                  Open in New Tab
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CompliancePersonDrawer
